/**
 * THE INVERSION — the owner writes the QUESTION, and the step's other three strings are
 * written from it.
 *
 * PURE. No Vue, no store, no I/O, no translator.
 *
 * ── WHY IT IS THIS WAY ROUND ──────────────────────────────────────────────────────────
 * `schema.ts:23` makes `instruction` `z.string().min(1).max(1000)` — REQUIRED and
 * non-empty on every `ask`, `choose` and `choose_live`. Something has to fill it, and the
 * obvious answer is to make it the primary box. That asks a shop owner to write prompt
 * prose on the screen he meets most often, and it leaves `questionEn`/`questionAr` — the
 * only two fields a WhatsApp CARD can carry — as a chore nobody does. `lang=ar` returning
 * `flow: null` with every step marked `no_question_in_language` is what "nobody does it"
 * looks like in production.
 *
 * So the box writes the QUESTION and this file writes:
 *   • `instruction` — prose FOR THE MODEL, so the chat path still works;
 *   • `name`        — the label the map, the trace and a `stayInChat` row use;
 *   • the save key  — `saveTo.chat`, which the schema refuses unless it is snake_case.
 *
 * Three fields an owner used to fill in by hand, and one of them (`bad_save_key`) was
 * "the one box on this card an owner can put a space in and get a refusal she cannot read".
 *
 * ── WHERE THE BOX WRITES ──────────────────────────────────────────────────────────────
 * A step that carries a question (`ask`, `choose` and, since P-10.24, `choose_live`) keeps
 * the box in `questionEn` and generates `instruction` from it. Any other step has no
 * question, so the box writes `instruction` DIRECTLY. A live list saved before P-10.24 still
 * has its words only in `instruction`; `task-spine-edit.js` `needTextOf` shows those. The
 * sidecar's schema is `.strict()`, so a question on a type that cannot hold one is REFUSED,
 * not dropped. `questionFieldFor` is that rule as data; `helpers/task-answer-kinds.js`
 * `carriesQuestion` is the same rule read from the row table, and the two agree by
 * construction because this file asks it.
 *
 * ── THE LEGACY RULE, WHICH IS THE WHOLE SAFETY OF THIS FILE ───────────────────────────
 * A stored step's `instruction` is somebody's writing. It is NEVER silently regenerated.
 * A generated string is recognised by being EXACTLY what this file would have produced for
 * the previous question — byte for byte — and only then is it rewritten. Anything else is
 * the owner's, is kept, and the screen offers to replace it rather than doing it.
 *
 * That is `OptimizeField`'s propose-and-accept discipline: the generator never edits in
 * place, for the same reason the model never does.
 */
import { carriesQuestion } from './task-answer-kinds.js';
import { MAX_NAME } from './task-flow-draft.js';

/**
 * Where the one box an owner types in lands, per step type and per LANGUAGE (P-7.1). A question
 * typed in Arabic is the Arabic question: `questionAr`, never `questionEn`. A step with no
 * question keeps writing `instruction`, whatever the language.
 */
export const questionFieldFor = (type, lang = 'en') => {
  if (!carriesQuestion(type)) return 'instruction';
  return lang === 'ar' ? 'questionAr' : 'questionEn';
};

/**
 * The language a step's question is written in first: Arabic when only the Arabic is there,
 * English otherwise. What a type change carries across, and what the box opens on.
 */
export const questionLangOf = step =>
  String(step?.questionEn ?? '').trim() === '' &&
  String(step?.questionAr ?? '').trim() !== ''
    ? 'ar'
    : 'en';

const collapse = value =>
  String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();

// ── the name ──────────────────────────────────────────────────────────────────────────

/**
 * The step's label: the question itself, without its question mark, inside `MAX_NAME`.
 *
 * `flow.ts:225` already relies on real names being question-shaped ("Which studio"), and
 * it is the one place a name reaches a customer — as a form field's label when the step
 * carries no question of its own. Generating it from the question is therefore not a
 * convenience; it is the only generator that makes that fallback correct by construction.
 */
export const nameFor = question => {
  const said = collapse(question).replace(/[?؟.!]+$/u, '');
  return said.slice(0, MAX_NAME);
};

// ── the instruction ───────────────────────────────────────────────────────────────────

/**
 * Prose for the MODEL, in English, whatever language the question is in.
 *
 * English because `instruction` is only ever interpolated into a prompt (`read.ts`,
 * `prompt.ts`) and one instruction has to serve a customer writing in either language —
 * the model phrases the question itself, in the customer's own language, every turn. It is
 * never sent to anybody.
 *
 * `''` for a blank question, deliberately: a blank `instruction` is `blank_instruction`,
 * which is what correctly holds the Steps screen open until the question is written.
 */
export const instructionFor = (type, question) => {
  const said = collapse(question);
  if (said === '') return '';
  const asked = said.replace(/[?؟]+$/u, '');
  if (type === 'choose') {
    return `Ask: ${asked}? Offer only the options given; do not invent any, and do not list them yourself.`;
  }
  return `Ask: ${asked}? Do not answer it yourself.`;
};

// ── the save key ──────────────────────────────────────────────────────────────────────

/**
 * Words that carry no subject. Stripping them is what turns "Which day would you like to
 * come in?" into `day` rather than into `which_day_would`.
 *
 * Deliberately small and deliberately English-only: the key is snake_case ASCII by
 * `schema.ts:21`, an Arabic question produces nothing usable from it, and the fallback
 * below is what that case is for. A longer list would guess more and be wrong more.
 */
const SKIP = new Set([
  'a',
  'an',
  'and',
  'are',
  'at',
  'be',
  'can',
  'come',
  'could',
  'did',
  'do',
  'does',
  'for',
  'from',
  'give',
  'have',
  'how',
  'in',
  'is',
  'it',
  'like',
  'may',
  'me',
  'much',
  'many',
  'my',
  'need',
  'of',
  'on',
  'or',
  'our',
  'please',
  'put',
  'shall',
  'should',
  'so',
  'tell',
  'the',
  'their',
  'them',
  'they',
  'to',
  'under',
  'us',
  'want',
  'was',
  'we',
  'were',
  'what',
  'when',
  'where',
  'which',
  'who',
  'why',
  'will',
  'with',
  'would',
  'you',
  'your',
]);

/** What `schema.ts:21` accepts: snake_case, starting with a letter, 1-32. */
const LEGAL = /^[a-z][a-z0-9_]{0,31}$/;

/** The bare candidate, before anything is done about collisions. `''` when nothing lands. */
export const saveKeyStem = question => {
  const words = collapse(question)
    .toLowerCase()
    .replace(/[^a-z0-9\s_]/g, ' ')
    .split(/[\s_]+/)
    .filter(Boolean);
  const first = words.find(word => !SKIP.has(word) && /^[a-z]/.test(word));
  if (!first) return '';
  return first.slice(0, 32);
};

/**
 * The key this answer is stored under — derived, deduped, and always legal.
 *
 * `taken` is every key the rest of the task already writes. A collision is suffixed rather
 * than refused: two questions about a day are a normal thing to write, and a builder that
 * answered "that name is taken" would be handing back the very box this exists to delete.
 *
 * The fallback is `answer`, `answer2`, … — which is also what an Arabic-only question
 * produces, and it is honest: the key is internal, the sentence on screen names the step.
 */
export const deriveSaveKey = (question, taken = []) => {
  const used = new Set(taken);
  const stem = saveKeyStem(question);
  const base = LEGAL.test(stem) ? stem : 'answer';
  if (!used.has(base)) return base;
  let n = 2;
  while (used.has(`${base}${n}`)) n += 1;
  return `${base}${n}`;
};

// ── the legacy rule ───────────────────────────────────────────────────────────────────

/**
 * May this stored string be rewritten when the question changes?
 *
 * ONLY when it is byte-for-byte what this file would have produced for the question that
 * is being replaced. A blank is also generated — there was nothing there to protect.
 *
 * Everything else is somebody's writing: a hand-written instruction on the live booking
 * task, a save key an owner renamed on purpose, a name from a graph written before this
 * screen existed. The caller shows "Your own wording is still being used" and offers the
 * swap; it never takes it.
 */
export const wasGenerated = (current, generated) =>
  collapse(current) === '' || current === generated;

export default deriveSaveKey;
